import { DefinitionDataSourceType } from "@wingspanhq/payments/dist/interfaces/requirement";
import type { insuranceMonitorSettingsApiSpec } from "@wingspanhq/wingspan-api-types";
import * as Yup from "yup";

import { RequirementDefinitionProfessionalLiabilityFormValues } from "../../../../types";
import { getBaseEditValues } from "../../../../utils/getBaseEditValues";
import { RequirementDefinitionTemplateForm } from "../../types";
import { FormPartialAcceptWingspanMasterPolicy } from "../FormPartialAcceptWingspanMasterPolicy";
import {
  AGGREGATE_LIMIT_MINIMUM_COMMON,
  DEDUCTIBLE_CAP_COMMON,
  FormPartialProfessionalLiability,
  PER_CLAIM_LIMIT_MINIMUM_COMMON
} from "../FormPartialProfessionalLiability";
import { WarningWingspanIsNotAgent } from "../WarningWingspanIsNotAgent";

/*
https://github.com/wingspanHQ/benefits/blob/staging/src/lib/interfaces/insuranceRequirement.ts
 */

export const PROFESSIONAL_LIABILITY_FORM: RequirementDefinitionTemplateForm<
  insuranceMonitorSettingsApiSpec.InsuranceCoverageMonitorSettings,
  RequirementDefinitionProfessionalLiabilityFormValues,
  insuranceMonitorSettingsApiSpec.CreateInsuranceCoverageMonitorSettingsRequest
> = {
  getDefaultSettingsValues: template => ({
    acceptWingspanMasterPolicy: true,
    contractorDeductibleMaximum: `${DEDUCTIBLE_CAP_COMMON}`,
    perClaimLimitMinimum: `${PER_CLAIM_LIMIT_MINIMUM_COMMON}`,
    totalCoverageLimitMinimum: `${AGGREGATE_LIMIT_MINIMUM_COMMON}`
  }),
  getEditSettingsValues(template, requirementDefinition, settings) {
    const coverage = settings.coverages.find(
      c => c.policyType === "ProfessionalLiability"
    );
    const perClaim = coverage?.limits?.find(l => l.limitType === "PerClaim");
    const aggregate = coverage?.limits?.find(
      l => l.limitType === "PolicyAggregate"
    );
    const deductible = coverage?.limits?.find(
      l => l.limitType === "Deductible"
    );
    const acceptWingspan =
      coverage?.acceptedSourceTypes?.includes("PlanEnrollment") ?? false;

    return {
      acceptWingspanMasterPolicy: acceptWingspan,
      contractorDeductibleMaximum: String(deductible?.amount ?? 0),
      perClaimLimitMinimum: String(perClaim?.amount ?? 0),
      totalCoverageLimitMinimum: String(aggregate?.amount ?? 0)
    };
  },
  mapDataSourceResponseToRequirementCreateRequest(response) {
    return {
      dataSourceId: response.insuranceCoverageMonitorSettingsId,
      dataSourceType: DefinitionDataSourceType.InsuranceCoverageMonitorSettings
    };
  },
  mapFormDataToDataSourceCreateRequest(values, template) {
    const {
      acceptWingspanMasterPolicy,
      perClaimLimitMinimum,
      totalCoverageLimitMinimum,
      contractorDeductibleMaximum,
      ...other
    } = values;

    return {
      ...other,
      coverages: [
        {
          acceptedSourceTypes: acceptWingspanMasterPolicy
            ? ["InsuranceImport", "PlanEnrollment"]
            : ["InsuranceImport"],
          appliedProvisions: [],
          limits: [
            {
              amount: Number(perClaimLimitMinimum),
              currency: "USD",
              limitType: "PerClaim"
            },
            {
              amount: Number(totalCoverageLimitMinimum),
              currency: "USD",
              limitType: "PolicyAggregate"
            },
            {
              amount: Number(contractorDeductibleMaximum),
              currency: "USD",
              limitType: "Deductible"
            }
          ],
          policyType: "ProfessionalLiability"
        }
      ],
      name: template.name
    };
  },
  partialForm: (
    <>
      <WarningWingspanIsNotAgent />
      <FormPartialAcceptWingspanMasterPolicy switchLabel="Accept Wingspan's PL/GL Master Policy" />
      <FormPartialProfessionalLiability />
    </>
  ),
  successCreationMessage: "Requirement created successfully",
  validationSchema: Yup.object().shape({
    acceptWingspanMasterPolicy: Yup.boolean().required("Required"),
    contractorDeductibleMaximum: Yup.string().required("Required"),
    perClaimLimitMinimum: Yup.string().required("Required"),
    totalCoverageLimitMinimum: Yup.string().required("Required")
  })
};
